import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasks } from '../hooks/useTasks';
import ProjectCards from '../components/projects/ProjectCards';
import { isProjectOverdue } from '../components/projects/projectMeta';
import { Switch } from '../components/common/Switch';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useProjects } from '../hooks/useProjects';
import { Project, ProjectFilters } from '../types/project';
import CommonPageHeader from '../components/common/CommonPageHeader';
import ProjectForm from '../components/projects/ProjectForm';
import LoadingScreen from '../components/common/LoadingScreen';
import { PlusIcon, ChartBarIcon, FunnelIcon, MagnifyingGlassIcon, ViewColumnsIcon } from '@heroicons/react/24/outline';

const Projects: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const {
    projects,
    loading,
    createProject,
    updateProject,
    deleteProject,
    updateProjectStatus,
    updateProjectPriority,
    getProjectStats
  } = useProjects();

  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ProjectFilters>({});
  const [showClosed, setShowClosed] = useState<boolean>(() => { try { return localStorage.getItem('projects.showClosed') === '1'; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem('projects.showClosed', showClosed ? '1' : '0'); } catch { /* ignore */ } }, [showClosed]);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const navigate = useNavigate();
  const { tasks } = useTasks();
  const taskCounts = useMemo(() => {
    const m: Record<string, { total: number; done: number; open: number }> = {};
    tasks.forEach(tk => {
      if (!tk.projectId) return;
      const e = (m[tk.projectId] ||= { total: 0, done: 0, open: 0 });
      if (tk.status === 'cancelled') return;
      e.total++;
      if (tk.status === 'completed') e.done++; else e.open++;
    });
    return m;
  }, [tasks]);

  // Effect to handle FAB trigger for new project
  useEffect(() => {
    const handleFABNewProject = () => {
      setEditingProject(null);
      setShowForm(true);
    };

    window.addEventListener('openProjectForm', handleFABNewProject);
    return () => {
      window.removeEventListener('openProjectForm', handleFABNewProject);
    };
  }, []);

  // Jogosultság ellenőrzés
  if (!hasAccess('projects')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.errors.loadingFailed')}</div>
      </div>
    );
  }

  // Projektek szűrése
  const filteredProjects = projects.filter(project => {
    // Szöveges keresés
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        project.name.toLowerCase().includes(searchLower) ||
        project.description.toLowerCase().includes(searchLower) ||
        project.customerName?.toLowerCase().includes(searchLower) ||
        project.tags.some(tag => tag.toLowerCase().includes(searchLower));
      
      if (!matchesSearch) return false;
    }

    // Státusz szűrés
    if (filters.status && project.status !== filters.status) return false;
    // Lezárt (befejezett / megszakított) projektek alapból rejtve, kivéve ha épp azokra szűrünk
    if (!showClosed && !filters.status && (project.status === 'completed' || project.status === 'cancelled')) return false;
    if (overdueOnly && !isProjectOverdue(project)) return false;

    // Prioritás szűrés
    if (filters.priority && project.priority !== filters.priority) return false;

    // Hozzárendelt felhasználó szűrés
    if (filters.assignedUser && !project.assignedUsers.includes(filters.assignedUser)) return false;

    // Ügyfél szűrés
    if (filters.customerId && project.customerId !== filters.customerId) return false;

    return true;
  });

  // Projekt létrehozás
  const handleCreateProject = async (projectData: any) => {
    try {
      await createProject(projectData);
      setShowForm(false);
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  };

  // Projekt frissítés
  const handleUpdateProject = async (projectData: any) => {
    if (!editingProject) return;
    
    try {
      await updateProject(editingProject.id, projectData);
      setEditingProject(null);
      setShowForm(false);
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  };

  // Projekt törlés
  const handleDeleteProject = async (projectId: string) => {
    if (window.confirm(t('projects.confirmDelete') || 'Biztosan törölni szeretné ezt a projektet?')) {
      try {
        await deleteProject(projectId);
      } catch (error) {
        console.error('Error deleting project:', error);
        alert(t('projects.deleteError') || 'Hiba történt a projekt törlése során');
      }
    }
  };

  // Projekt szerkesztés
  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setShowForm(true);
  };

  // Projekt megtekintés
  // Státusz frissítés
  const handleUpdateStatus = async (projectId: string, status: Project['status']) => {
    try {
      await updateProjectStatus(projectId, status);
      toast.success(t('projects.messages.statusUpdated') || 'Státusz sikeresen frissítve!');
    } catch (error) {
      console.error('Error updating project status:', error);
      toast.error(t('projects.messages.statusUpdateError') || 'Hiba történt a státusz frissítése során');
    }
  };

  // Prioritás frissítés
  const handleUpdatePriority = async (projectId: string, priority: Project['priority']) => {
    try {
      await updateProjectPriority(projectId, priority);
      toast.success(t('projects.messages.priorityUpdated') || 'Prioritás sikeresen frissítve!');
    } catch (error) {
      console.error('Error updating project priority:', error);
      toast.error(t('projects.messages.priorityUpdateError') || 'Hiba történt a prioritás frissítése során');
    }
  };

  // Szűrők resetelése
  const resetFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  // Statisztikák
  const stats = getProjectStats();

  if (loading) {
    return <LoadingScreen />;
  }

  const kpi = [
    { key: '', label: 'Összes', n: stats.total },
    { key: 'active', label: 'Aktív', n: stats.active, dot: 'bg-sky-500' },
    { key: 'planning', label: 'Tervezés', n: projects.filter(p => p.status === 'planning').length, dot: 'bg-gray-400' },
    { key: 'completed', label: 'Befejezett', n: stats.completed, dot: 'bg-emerald-500' },
    { key: 'overdue', label: 'Lejárt', n: stats.overdueCount, dot: 'bg-rose-500' }
  ];
  const activeKpi = overdueOnly ? 'overdue' : (filters.status || '');

  return (
    <div className="space-y-4">
      <CommonPageHeader
        title={t('projects.title') || 'Projektek'}
        subtitle={t('projects.subtitle') || 'Projektjeinek kezelése és nyomon követése'}
        icon={ChartBarIcon}
      >
        <button
          onClick={() => navigate('/project-board')}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <ViewColumnsIcon className="h-4 w-4 mr-2" />
          <span>Több projekt – Gantt</span>
        </button>
        <button
          onClick={() => { setEditingProject(null); setShowForm(true); }}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          <span>{t('projects.new') || 'Új projekt'}</span>
        </button>
      </CommonPageHeader>

      {/* KPI csempék – kattintva szűrnek */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {kpi.map(tile => (
          <button
            key={tile.key || 'all'}
            onClick={() => { if (tile.key === 'overdue') { setOverdueOnly(true); setFilters(prev => ({ ...prev, status: undefined })); } else { setOverdueOnly(false); setFilters(prev => ({ ...prev, status: (tile.key || undefined) as Project['status'] | undefined })); } }}
            className={`crm-card px-3 py-2 text-left transition-colors ${activeKpi === tile.key ? 'border-gray-900 dark:border-white' : 'hover:border-gray-300 dark:hover:border-gray-600'}`}
          >
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 min-w-0">
              {tile.dot && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${tile.dot}`} />}
              <span className="truncate">{tile.label}</span>
            </div>
            <div className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-50 leading-tight">{tile.n}</div>
          </button>
        ))}
      </div>

      {/* Szűrők */}
      <div className="crm-card p-3 sm:p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={t('projects.searchPlaceholder') || 'Keresés projektekben…'} className="w-full h-9 pl-9 pr-3 text-sm border rounded-lg" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <select value={overdueOnly ? '' : (filters.status || '')} onChange={(e) => { setOverdueOnly(false); setFilters(prev => ({ ...prev, status: (e.target.value || undefined) as Project['status'] | undefined })); }} className="h-9 pl-3 pr-8 text-sm border rounded-lg">
              <option value="">Minden állapot</option>
              <option value="planning">Tervezés</option>
              <option value="active">Aktív</option>
              <option value="paused">Szüneteltetve</option>
              <option value="completed">Befejezett</option>
              <option value="cancelled">Megszakítva</option>
            </select>
            <select value={filters.priority || ''} onChange={(e) => setFilters(prev => ({ ...prev, priority: (e.target.value || undefined) as Project['priority'] | undefined }))} className="h-9 pl-3 pr-8 text-sm border rounded-lg">
              <option value="">Minden prioritás</option>
              <option value="low">Alacsony</option>
              <option value="medium">Közepes</option>
              <option value="high">Magas</option>
              <option value="urgent">Sürgős</option>
            </select>
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
              <Switch checked={!!filters.assignedUser} onChange={(v) => setFilters(prev => ({ ...prev, assignedUser: v ? (user as any)?.id : undefined }))} size="sm" />
              Csak az enyéim
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap" title="Befejezett és megszakított projektek mutatása">
              <Switch checked={showClosed} onChange={setShowClosed} size="sm" />
              Lezártak is
            </label>
          </div>
          <div className="md:ml-auto text-xs text-gray-500 dark:text-gray-400 tabular-nums">{filteredProjects.length} / {projects.length} projekt</div>
        </div>
      </div>

      <ProjectCards
        projects={filteredProjects}
        onOpen={(p) => navigate(`/projects/${p.id}`)}
        onEdit={handleEditProject}
        onDelete={handleDeleteProject}
        onUpdateStatus={handleUpdateStatus}
        taskCounts={taskCounts}
      />

      {/* Projekt form modal */}
      <ProjectForm
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingProject(null);
        }}
        onSubmit={editingProject ? handleUpdateProject : handleCreateProject}
        initialData={editingProject || undefined}
      />

    </div>
  );
};

export default Projects; 